import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import CurriculumBuilder from '../components/curriculum/CurriculumBuilder';
import { normalizeCurriculumOutline } from '../components/curriculum/curriculumUtils';
import PageTemplate from './PageTemplate';
import { buildCourseRequestData } from '../utils/courseFormUtils';
import './ModulePage.css';

function formatDecimalFee(fee) {
  const num = Number(fee);
  if (Number.isNaN(num)) {
    return fee;
  }
  return num.toFixed(2);
}

function EditCourse() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    course_name: '',
    course_fee: '',
  });
  const [curriculumOutline, setCurriculumOutline] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadCourse() {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get(`/api/courses/${id}/`);
        const course = res.data?.course || res.data;

        setForm({
          course_name: course.course_name || course.name || '',
          course_fee: course.course_fee || course.fee || '',
        });
        setCurriculumOutline(normalizeCurriculumOutline(course.curriculum_outline));
      } catch (e) {
        console.error('Error loading course:', e);
        if (e?.response?.status === 404) {
          setError('Course not found.');
          setTimeout(() => navigate('/courses'), 2000);
        } else if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError(
            e?.response?.data?.detail ||
              e?.response?.data?.message ||
              'Unable to load course. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadCourse();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedName = form.course_name.trim();
    const trimmedFee = form.course_fee.toString().trim();

    if (!trimmedName || !trimmedFee) {
      setError('Please enter both course name and course fee.');
      return;
    }

    const numericFee = Number(trimmedFee);
    if (Number.isNaN(numericFee) || numericFee <= 0) {
      setError('Please enter a valid course fee (greater than 0).');
      return;
    }

    setSubmitting(true);

    try {
      const formattedFee = formatDecimalFee(trimmedFee);
      const body = buildCourseRequestData({
        course_name: trimmedName,
        course_fee: formattedFee,
        curriculum_outline: curriculumOutline,
      });
      await apiClient.patch(`/api/courses/${id}/`, body);

      setSuccess('Course updated successfully.');
      setTimeout(() => navigate('/courses'), 600);
    } catch (err) {
      console.error('Course update error:', err);
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        Object.values(err?.response?.data || {})
          .flat()
          .find((msg) => typeof msg === 'string') ||
        err?.message ||
        'Unable to update course. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate title="Edit Course" subtitle="Update course details">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading course details...</p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Edit Course" subtitle="Update course details">
      <div className="add-teacher-panel">
        <div className="add-teacher-header">
          <div>
            <h2>Course details</h2>
            <p>Update course name, fee, and curriculum outline.</p>
          </div>
          <span className="add-teacher-badge">PATCH /api/courses/:id/</span>
        </div>

        <form className="add-teacher-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="course_name">Course Name</label>
            <input
              id="course_name"
              name="course_name"
              type="text"
              placeholder="e.g. Web Development Basics"
              value={form.course_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="course_fee">Course Fee</label>
            <input
              id="course_fee"
              name="course_fee"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 2500"
              value={form.course_fee}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group form-group-wide curriculum-slot">
            <CurriculumBuilder
              courseTitle={form.course_name}
              value={curriculumOutline}
              onChange={setCurriculumOutline}
            />
          </div>

          {error && <p className="form-error form-group-wide">{error}</p>}
          {success && <p className="form-success form-group-wide">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/courses')}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update Course'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default EditCourse;
